import React, { useEffect, useState } from 'react';
import {
  Wrapper,
  Button,
  useBreadcrumbRoutes,
  FormTitle,
  Form,
  ShowInput,
  Tag,
  SubContent,
  Tooltip,
} from '@maxtropy/components';
import { Space, Row, Col } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay } from '@/shared/types';
import { CycleType } from './service';
import {
  apiV2DeviationRateAnalysisConfigRuleQueryPost,
  V2DeviationRateAnalysisConfigRuleQueryPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const DeviationAlarmDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [info, setInfo] = useState<V2DeviationRateAnalysisConfigRuleQueryPostResponse>();

  useEffect(() => {
    if (id) {
      apiV2DeviationRateAnalysisConfigRuleQueryPost({ id }).then(res => {
        setInfo(res);
      });
    }
  }, [id]);

  const routes = [{ name: '查看规则' }];

  const handleBack = () => {
    navigate('/deviation/server/alarm');
  };

  // 格式化统计周期
  const formatCycleType = () => {
    if (!info?.statCycleType) return '--';
    const typeMap: Record<string, string> = {
      [CycleType.date]: '按天',
      [CycleType.hour]: '按小时',
    };
    const unitMap: Record<string, string> = {
      [CycleType.date]: '天',
      [CycleType.hour]: '小时',
    };
    const type = info.statCycleType;
    const value = info.statCycleTime;
    return `${typeMap[type] || type} ${value || ''}${unitMap[type] || ''}`;
  };

  // 格式化拓扑节点
  const formatTopologyNodes = () => {
    if (!info?.topologyNodes?.length) return '--';
    return info.topologyNodes.map(node => node.nodeName).join('、');
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}>
      <FormTitle title="查看规则"></FormTitle>

      <SubContent style={{ marginBottom: 0 }}>
        <Form layout="vertical">
          {/* 第一行：规则名称、报警等级、偏差率 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="规则名称">
                <ShowInput value={info?.ruleName}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="报警等级">
                {info?.alarmLevel !== undefined && info?.alarmLevel !== null ? (
                  <Tag border="solid" color={AlarmLevelColorDisplay[info.alarmLevel as AlarmLevel]}>
                    {AlarmLevelDisplay[info.alarmLevel as AlarmLevel]}
                  </Tag>
                ) : (
                  '--'
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span>
                    偏差率&nbsp;
                    <Tooltip
                      overlayInnerStyle={{ width: 320 }}
                      title="根据设定的统计周期, 查询统计周期内所选择能源介质拓扑对应的拓扑节点对应的能源介质的偏差率绝对值, 若大于等于输入的值, 则报警；"
                    >
                      <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                    </Tooltip>
                  </span>
                }
              >
                <ShowInput value={info?.deviationRate ? `${info.deviationRate}%` : '--'}></ShowInput>
              </Form.Item>
            </Col>
          </Row>

          {/* 第二行：统计周期、能源介质拓扑、拓扑节点 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="统计周期">
                <ShowInput value={formatCycleType()}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="能源介质拓扑">
                <ShowInput value={info?.energyMediumTopologyName}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="拓扑节点">
                <ShowInput value={formatTopologyNodes()}></ShowInput>
              </Form.Item>
            </Col>
          </Row>

          {/* 第三行：能源介质 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="能源介质">
                <ShowInput value={info?.energyMediumName}></ShowInput>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </SubContent>

      <Space size={8} className="sticky-footer">
        <Button onClick={handleBack}>返回</Button>
      </Space>
    </Wrapper>
  );
};

export default DeviationAlarmDetail;
